/**
 * Branch selection mode lives in the agent's `settings` jsonb (key
 * `branch_selection_mode`), which is a different object from
 * `provider_settings` (channel credentials).
 *
 * It controls what the agent does when several branches can serve the
 * customer's address: pick the closest open one, or list them so the customer
 * chooses.
 *
 * The API treats a missing `settings` object, a missing key and a null value as
 * the same thing: the `auto` default.
 */

export const BRANCH_SELECTION_MODE_AUTO = "auto";
export const BRANCH_SELECTION_MODE_MARKETPLACE = "marketplace";

export const BRANCH_SELECTION_MODE_DEFAULT = BRANCH_SELECTION_MODE_AUTO;

export const BRANCH_SELECTION_MODE_OPTIONS = [
  { value: BRANCH_SELECTION_MODE_AUTO },
  { value: BRANCH_SELECTION_MODE_MARKETPLACE },
];

const VALID_MODES = new Set(
  BRANCH_SELECTION_MODE_OPTIONS.map((option) => option.value),
);

/**
 * Normalize any stored/selected value to one the API accepts.
 * The API is case-sensitive and rejects empty strings, so anything unexpected
 * falls back to the default instead of being sent through.
 * @param {unknown} value
 * @returns {string} `auto` or `marketplace`
 */
export const normalizeBranchSelectionMode = (value) => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();

  return VALID_MODES.has(normalized)
    ? normalized
    : BRANCH_SELECTION_MODE_DEFAULT;
};

/**
 * Read the effective mode of an agent, collapsing the three "not set" cases
 * (no `settings`, no key, null key) into the default.
 * @param {Object} agent - Agent resource
 * @returns {string} `auto` or `marketplace`
 */
export const getAgentBranchSelectionMode = (agent) =>
  normalizeBranchSelectionMode(agent?.settings?.branch_selection_mode);

/**
 * Build the `settings` payload for the create/update endpoints.
 * The API merges `settings` per key, so sending only this key leaves any other
 * key untouched.
 * @param {string} mode
 * @returns {{ branch_selection_mode: string }}
 */
export const buildBranchSelectionSettings = (mode) => ({
  branch_selection_mode: normalizeBranchSelectionMode(mode),
});
